import { useEffect, useCallback, useRef } from 'react';
import { Routes, Route } from 'react-router-dom';
import { useInvoiceStore } from './stores/invoiceStore';
import { useAuthStore } from './stores/authStore';
import LandingPage from './pages/LandingPage';
import ContactPage from './pages/ContactPage';
import FeaturesPage from './pages/FeaturesPage';
import FaqPage from './pages/FaqPage';
import PricingPage from './pages/PricingPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import NotFoundPage from './pages/NotFoundPage';
import InvoiceStatusPage from './pages/InvoiceStatusPage';
import { AdminPage } from './pages/AdminPage';
import { UserInvoices } from './pages/admin/UserInvoices';
import { UserCompanies } from './pages/admin/UserCompanies';
import { UserSettings } from './pages/admin/UserSettings';
import { UserSchedules } from './pages/admin/UserSchedules';
import { EmailQueue } from './pages/admin/EmailQueue';
import { InvoiceDetails } from './pages/admin/InvoiceDetails';
import { AuthProvider } from './components/Auth/AuthProvider';
import { ClientOnlyLoginModal } from './components/Auth/LoginModal';
import { AdminRoute } from './components/Auth/AdminRoute';
import { SUPPORTED_LANGUAGES } from './i18n/LanguageContext';

export default function App() {
  const { loadInvoices, syncWithCloud, loadingState, error } = useInvoiceStore();
  const { user, loading: authLoading } = useAuthStore();
  const initialLoadDone = useRef(false);
  const syncInProgress = useRef(false);

  // Handle initial data loading - only on mount
  useEffect(() => {
    if (!initialLoadDone.current) {
      console.log('[App] Initial load - loading local data...');
      loadInvoices().catch(error => {
        console.error('[App] Failed to load local data:', error);
      });
      initialLoadDone.current = true;
    }
  }, []); // Only run on mount

  // Handle cloud sync when auth state changes
  const handleCloudSync = useCallback(async () => {
    if (!user || authLoading || syncInProgress.current) return;

    try {
      syncInProgress.current = true;
      console.log('[App] Starting cloud sync...');
      await syncWithCloud();
      console.log('[App] Cloud sync complete');
    } catch (error) {
      console.error('[App] Cloud sync failed:', error);
    } finally {
      syncInProgress.current = false;
    }
  }, [user, authLoading, syncWithCloud]);

  // Trigger cloud sync when auth state changes
  useEffect(() => {
    if (user && !authLoading && !syncInProgress.current) {
      handleCloudSync();
    }
  }, [user, authLoading, handleCloudSync]);

  // Log state changes for debugging
  useEffect(() => {
    console.log('[App] State update:', { 
      loadingState, 
      isAuthenticated: !!user, 
      authLoading,
      error,
      syncInProgress: syncInProgress.current
    });
  }, [loadingState, user, authLoading, error]);

  return (
    <AuthProvider>
      <Routes>
        {/* Default language routes (English) */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/features" element={<FeaturesPage />} />
        <Route path="/faq" element={<FaqPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/terms" element={<TermsOfServicePage />} />
        
        {/* Invoice status page - public route */}
        <Route path="/invoice-status/:id" element={<InvoiceStatusPage />} />
        
        {/* Admin routes */}
        <Route path="/admin" element={
          <AdminRoute>
            <AdminPage />
          </AdminRoute>
        } />
        <Route path="/admin/user/:uid/invoices" element={
          <AdminRoute>
            <UserInvoices />
          </AdminRoute>
        } />
        <Route path="/admin/user/:uid/companies" element={
          <AdminRoute>
            <UserCompanies />
          </AdminRoute>
        } />
        <Route path="/admin/user/:uid/settings" element={
          <AdminRoute>
            <UserSettings />
          </AdminRoute>
        } />
        <Route path="/admin/user/:uid/schedules" element={
          <AdminRoute>
            <UserSchedules />
          </AdminRoute>
        } />
        <Route path="/admin/user/:uid/invoice/:invoiceId" element={
          <AdminRoute>
            <InvoiceDetails />
          </AdminRoute>
        } />
        <Route path="/admin/email-queue" element={
          <AdminRoute>
            <EmailQueue />
          </AdminRoute>
        } />
        
        {/* Localized routes for other languages */}
        {SUPPORTED_LANGUAGES.filter(lang => lang !== 'en').map(lang => (
          <Route key={lang} path={`/${lang}`} element={<LandingPage />} />
        ))}
        {SUPPORTED_LANGUAGES.filter(lang => lang !== 'en').map(lang => (
          <Route key={`${lang}-contact`} path={`/${lang}/contact`} element={<ContactPage />} />
        ))}
        {SUPPORTED_LANGUAGES.filter(lang => lang !== 'en').map(lang => (
          <Route key={`${lang}-features`} path={`/${lang}/features`} element={<FeaturesPage />} />
        ))}
        {SUPPORTED_LANGUAGES.filter(lang => lang !== 'en').map(lang => (
          <Route key={`${lang}-faq`} path={`/${lang}/faq`} element={<FaqPage />} />
        ))}
        {SUPPORTED_LANGUAGES.filter(lang => lang !== 'en').map(lang => (
          <Route key={`${lang}-pricing`} path={`/${lang}/pricing`} element={<PricingPage />} />
        ))}
        {SUPPORTED_LANGUAGES.filter(lang => lang !== 'en').map(lang => (
          <Route key={`${lang}-privacy`} path={`/${lang}/privacy`} element={<PrivacyPolicyPage />} />
        ))}
        {SUPPORTED_LANGUAGES.filter(lang => lang !== 'en').map(lang => (
          <Route key={`${lang}-terms`} path={`/${lang}/terms`} element={<TermsOfServicePage />} />
        ))}
        
        {/* 404 Page */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      <ClientOnlyLoginModal />
    </AuthProvider>
  );
}
